import React, { useEffect } from 'react';
import {
    Box,
    Button,
    VStack,
    Text,
    HStack,
    Container,
    Flex,
    Center,
    Image,
    Input,
    IconButton,
    useColorModeValue,
    Badge,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../Utils/AuthContext';
import { getAllCardsApi, normalizeCards, fetchPassportBlob, updateCardFieldApi } from '../Utils/ApiCall';
import { BusinessCard } from '../Utils/Storage';
import Navbar from '../Components/Navbar';
import { EditIcon, CheckIcon, CloseIcon, ViewIcon } from '@chakra-ui/icons';
import { getInitials } from '../Utils/Helpers';
import { useCompany } from '../Utils/CompanyContext';

const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
    const config: Record<string, { bg: string; color: string; dot: string }> = {
        Approved: { bg: 'rgba(34, 197, 94, 0.15)', color: '#22C55E', dot: '#22C55E' },
        Rejected: { bg: 'rgba(239, 68, 68, 0.15)', color: '#EF4444', dot: '#EF4444' },
        Pending: { bg: 'rgba(245, 158, 11, 0.15)', color: '#F59E0B', dot: '#F59E0B' },
    };
    const c = config[status] ?? config['Pending'];
    return (
        <HStack
            spacing={1.5}
            display="inline-flex"
            bg={c.bg} px={3} py={1} borderRadius="full"
            backdropFilter="blur(8px)"
            border="1px solid"
            borderColor="whiteAlpha.200"
        >
            <Box w="6px" h="6px" borderRadius="full" bg={c.dot} flexShrink={0} />
            <Text fontSize="11px" fontWeight={700} color={c.color}
                textTransform="uppercase" letterSpacing="0.08em">
                {status}
            </Text>
        </HStack>
    );
};

const DetailItem: React.FC<{ label: string; value: string }> = ({ label, value }) => {
    const labelColor = useColorModeValue('gray.500', 'gray.400');
    const valueColor = useColorModeValue('#0F172A', '#F8FAFC');
    return (
        <Box>
            <Text fontSize="10.5px" fontWeight={700} color={labelColor}
                textTransform="uppercase" letterSpacing="0.08em" mb={1}>
                {label}
            </Text>
            <Text fontSize="13.5px" fontWeight={600} color={valueColor}>{value}</Text>
        </Box>
    );
};

const UserDashboard: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { company } = useCompany();
    const [userCard, setUserCard] = React.useState<BusinessCard | null>(null);
    const [photoUrl, setPhotoUrl] = React.useState<string | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [editingPhone, setEditingPhone] = React.useState(false);
    const [phoneValue, setPhoneValue] = React.useState('');
    const [savingPhone, setSavingPhone] = React.useState(false);

    const pageBg = useColorModeValue('#F4F6FB', '#070C18');
    const cardBg = useColorModeValue('#FFFFFF', '#0D1527');
    const cardBorder = useColorModeValue('rgba(13, 31, 76, 0.08)', 'rgba(255, 255, 255, 0.08)');
    const cardShadow = useColorModeValue(
        '0 20px 40px -15px rgba(13, 31, 76, 0.1)',
        '0 20px 40px -15px rgba(0, 0, 0, 0.6)'
    );
    const headingColor = useColorModeValue('#0B132B', '#F8FAFC');
    const subtextColor = useColorModeValue('gray.600', 'gray.400');
    const supervisorBtnBorder = useColorModeValue('brand.500', 'brand.300');
    const supervisorBtnColor = useColorModeValue('brand.600', 'brand.200');
    const supervisorBtnHoverBg = useColorModeValue('brand.50', 'whiteAlpha.100');
    const emptyTitleColor = useColorModeValue('#0F172A', '#F8FAFC');
    const emptyDescColor = useColorModeValue('gray.600', 'gray.400');
    const rejectedBannerBg = useColorModeValue('red.50', 'rgba(239, 68, 68, 0.1)');
    const rejectedBannerBorder = useColorModeValue('red.200', 'rgba(239, 68, 68, 0.3)');
    const rejectedIconBg = useColorModeValue('red.100', 'rgba(239, 68, 68, 0.2)');
    const rejectedTitleColor = useColorModeValue('red.800', 'red.300');
    const rejectedTextColor = useColorModeValue('red.700', 'red.200');
    const deptTextColor = useColorModeValue('gray.600', 'gray.400');
    const unitTextColor = useColorModeValue('gray.500', 'gray.500');
    const dividerColor = useColorModeValue('gray.100', 'whiteAlpha.100');
    const phoneLabelColor = useColorModeValue('gray.500', 'gray.400');
    const editPhoneBtnColor = useColorModeValue('gray.400', 'gray.500');
    const phoneInputBorder = useColorModeValue('gray.300', 'whiteAlpha.300');
    const phoneTextColor = useColorModeValue('#0F172A', '#F8FAFC');
    const footerBorder = useColorModeValue('gray.100', 'whiteAlpha.100');
    const resubmitBtnHoverBg = useColorModeValue('red.50', 'whiteAlpha.100');
    const approvedBannerBg = useColorModeValue('green.50', 'rgba(34, 197, 94, 0.1)');
    const approvedBannerBorder = useColorModeValue('green.200', 'rgba(34, 197, 94, 0.3)');
    const approvedIconBg = useColorModeValue('green.100', 'rgba(34, 197, 94, 0.2)');
    const approvedTitleColor = useColorModeValue('green.800', 'green.300');
    const approvedTextColor = useColorModeValue('green.700', 'green.200');

    const loadCard = React.useCallback(async () => {
        try {
            const res = await getAllCardsApi();
            if (res.data.success) {
                const cards = normalizeCards(res.data.data);
                const myCard = cards.find(c => c.email === user!.email);
                if (myCard) {
                    setUserCard(myCard);
                    if (myCard.photo) {
                        const lookupKey = myCard.photo.split('/').pop() || myCard.id;
                        const blobUrl = await fetchPassportBlob(lookupKey);
                        if (blobUrl) setPhotoUrl(blobUrl);
                    }
                }
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [user]);

    useEffect(() => {
        if (!user) return;
        loadCard();
        const interval = setInterval(() => {
            loadCard();
        }, 60000);
        return () => clearInterval(interval);
    }, [user, loadCard]);

    return (
        <Box bg={pageBg} minH="100vh" fontFamily="'Inter', sans-serif" transition="background 0.25s ease">
            {/* Navbar */}
            <Navbar />

            {/* Page content */}
            <Container maxW="980px" py={{ base: 6, md: 10 }}>
                <VStack spacing={7} align="stretch">

                    {/* Page header */}
                    <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
                        <Box>
                            <Text
                                fontFamily="'Cormorant Garamond', Georgia, serif"
                                fontSize={{ base: "30px", md: "38px" }}
                                fontWeight={400}
                                color={headingColor}
                                letterSpacing="-0.3px"
                            >
                                Digital Identity
                            </Text>
                            <Text fontSize="13px" color={subtextColor}>
                                Manage your official {company.companyName} corporate card
                            </Text>
                        </Box>

                        {/* Supervisor switch button if permitted */}
                        {(user?.role === 'Supervisor' || user?.role === 'Admin') && (
                            <Button
                                size="sm"
                                variant="outline"
                                borderColor={supervisorBtnBorder}
                                color={supervisorBtnColor}
                                fontSize="12.5px"
                                fontWeight={600}
                                borderRadius="10px"
                                px={4}
                                _hover={{ bg: supervisorBtnHoverBg }}
                                onClick={() => navigate('/supervisor')}
                            >
                                ← Supervisor Queue
                            </Button>
                        )}
                    </Flex>

                    {/* Empty state */}
                    {!loading && !userCard && (
                        <Box
                            bg={cardBg}
                            border="1px solid"
                            borderColor={cardBorder}
                            boxShadow={cardShadow}
                            borderRadius="20px"
                            py={16}
                            px={6}
                            textAlign="center"
                        >
                            <Center
                                w="56px"
                                h="56px"
                                borderRadius="16px"
                                bg="linear-gradient(135deg, rgba(18, 100, 232, 0.1), rgba(0, 180, 216, 0.1))"
                                mx="auto"
                                mb={4}
                            >
                                <svg width="24" height="24" viewBox="0 0 22 22" fill="none">
                                    <rect x="2" y="4" width="18" height="14" rx="2.5" stroke="#1264E8" strokeWidth="1.5" />
                                    <path d="M6 9h10M6 13h6" stroke="#1264E8" strokeWidth="1.5" strokeLinecap="round" />
                                </svg>
                            </Center>
                            <Text
                                fontFamily="'Cormorant Garamond', Georgia, serif"
                                fontSize="24px"
                                fontWeight={400}
                                color={emptyTitleColor}
                                mb={1}
                            >
                                No card generated yet
                            </Text>
                            <Text fontSize="13px" color={emptyDescColor} mb={6}>
                                Create your verified digital card with custom QR code and vCard integration.
                            </Text>
                            <Button
                                bg="linear-gradient(135deg, #0B2C6D 0%, #1255C8 100%)"
                                color="white"
                                borderRadius="12px"
                                fontSize="13.5px"
                                fontWeight={600}
                                px={7}
                                h="46px"
                                boxShadow="0 4px 14px rgba(18, 85, 200, 0.3)"
                                _hover={{
                                    bg: 'linear-gradient(135deg, #0E398C 0%, #1664EC 100%)',
                                    transform: 'translateY(-1px)',
                                }}
                                onClick={() => navigate('/create')}
                            >
                                Create Digital Card
                            </Button>
                        </Box>
                    )}

                    {/* Card with data */}
                    {!loading && userCard && (
                        <VStack spacing={4} align="stretch">

                            {/* Rejected alert */}
                            {userCard.status === 'Rejected' && (
                                <HStack
                                    spacing={4}
                                    align="flex-start"
                                    bg={rejectedBannerBg}
                                    border="1px solid"
                                    borderColor={rejectedBannerBorder}
                                    borderRadius="14px"
                                    p={4}
                                >
                                    <Center
                                        w="34px"
                                        h="34px"
                                        borderRadius="10px"
                                        bg={rejectedIconBg}
                                        flexShrink={0}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 14 14" fill="none">
                                            <path d="M7 4v4M7 9.5v.5" stroke="#EF4444" strokeWidth="1.5" strokeLinecap="round" />
                                            <circle cx="7" cy="7" r="5.5" stroke="#EF4444" strokeWidth="1.3" />
                                        </svg>
                                    </Center>
                                    <Box>
                                        <Text fontSize="13.5px" fontWeight={700} color={rejectedTitleColor} mb={0.5}>
                                            Card Requires Revision
                                        </Text>
                                        <Text fontSize="12.5px" color={rejectedTextColor} lineHeight={1.5}>
                                            {userCard.rejectionReason || 'Please review your details and submit again.'}
                                        </Text>
                                    </Box>
                                </HStack>
                            )}

                            {/* The Executive Card Container */}
                            <Box
                                bg={cardBg}
                                border="1px solid"
                                borderColor={cardBorder}
                                borderRadius="20px"
                                boxShadow={cardShadow}
                                overflow="hidden"
                            >
                                {/* Multi-Color Top Brand Stripe */}
                                <Box
                                    h="3px"
                                    w="full"
                                    bg="linear-gradient(90deg, #00B4D8 0%, #1565C0 35%, #FFB703 70%, #F35B04 100%)"
                                />

                                {/* Executive Header Banner */}
                                <Flex
                                    bg="linear-gradient(135deg, #050D21 0%, #092258 55%, #0E3E9C 100%)"
                                    px={{ base: 6, md: 8 }}
                                    py={{ base: 5, md: 6 }}
                                    align="center"
                                    justify="space-between"
                                >
                                    <HStack spacing={4}>
                                        <Box
                                            boxSize="48px"
                                            borderRadius="full"
                                            overflow="hidden"
                                            border="2px solid white"
                                            p="2px"
                                            bg="linear-gradient(135deg, #00B4D8, #FFB703)"
                                            flexShrink={0}
                                        >
                                            {photoUrl ? (
                                                <Image
                                                    src={photoUrl}
                                                    alt={userCard.name}
                                                    boxSize="full"
                                                    borderRadius="full"
                                                    objectFit="cover"
                                                />
                                            ) : (
                                                <Center boxSize="full" borderRadius="full" bg="#0B2050">
                                                    <Text fontSize="14px" fontWeight={700} color="white">
                                                        {getInitials(userCard.name)}
                                                    </Text>
                                                </Center>
                                            )}
                                        </Box>

                                        <Box>
                                            <Text
                                                fontFamily="'Cormorant Garamond', Georgia, serif"
                                                fontSize={{ base: "22px", md: "26px" }}
                                                fontWeight={400}
                                                color="white"
                                                letterSpacing="-0.2px"
                                                lineHeight={1.1}
                                            >
                                                {userCard.name}
                                            </Text>
                                            <Text fontSize="11px" color="whiteAlpha.800" fontWeight={600} letterSpacing="0.06em" textTransform="uppercase">
                                                {userCard.role}
                                            </Text>
                                        </Box>
                                    </HStack>

                                    <StatusBadge status={userCard.status} />
                                </Flex>

                                {/* Role, Department, Company */}
                                <Box px={{ base: 6, md: 8 }} pt={5}>
                                    <HStack spacing={2} align="center" flexWrap="wrap">
                                        <Badge
                                            px={3}
                                            py={1}
                                            borderRadius="full"
                                            bg="linear-gradient(135deg, #071536 0%, #0E3585 100%)"
                                            color="white"
                                            fontSize="10.5px"
                                            fontWeight={700}
                                            letterSpacing="0.08em"
                                        >
                                            {userCard.company || company.companyName}
                                        </Badge>
                                        <Text fontSize="12.5px" color={deptTextColor}>
                                            • {userCard.department}
                                        </Text>
                                        {userCard.unit && (
                                            <Text fontSize="12.5px" color={unitTextColor}>
                                                (Unit: {userCard.unit})
                                            </Text>
                                        )}
                                    </HStack>
                                </Box>

                                {/* Divider */}
                                <Box h="1px" bg={dividerColor} mx={{ base: 6, md: 8 }} my={5} />

                                {/* Detail Grid */}
                                <Box
                                    px={{ base: 6, md: 8 }}
                                    display="grid"
                                    gridTemplateColumns={{ base: '1fr', sm: '1fr 1fr' }}
                                    gap={5}
                                >
                                    <DetailItem label="Email" value={userCard.email} />

                                    {/* Editable Phone */}
                                    <Box>
                                        <HStack spacing={1.5} mb={1}>
                                            <Text
                                                fontSize="10.5px"
                                                fontWeight={700}
                                                color={phoneLabelColor}
                                                textTransform="uppercase"
                                                letterSpacing="0.08em"
                                            >
                                                Phone Number
                                            </Text>
                                            {!editingPhone && userCard.status !== 'Rejected' && (
                                                <IconButton
                                                    aria-label="Edit phone"
                                                    icon={<EditIcon />}
                                                    size="xs"
                                                    variant="ghost"
                                                    color={editPhoneBtnColor}
                                                    _hover={{ color: 'brand.500' }}
                                                    onClick={() => { setPhoneValue(userCard.phone); setEditingPhone(true); }}
                                                />
                                            )}
                                        </HStack>
                                        {editingPhone ? (
                                            <HStack spacing={2}>
                                                <Input
                                                    size="sm"
                                                    value={phoneValue}
                                                    onChange={(e) => setPhoneValue(e.target.value)}
                                                    fontSize="13px"
                                                    borderRadius="8px"
                                                    borderColor={phoneInputBorder}
                                                    _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px #1264E8' }}
                                                />
                                                <IconButton
                                                    aria-label="Save"
                                                    icon={<CheckIcon />}
                                                    size="xs"
                                                    colorScheme="green"
                                                    isLoading={savingPhone}
                                                    onClick={async () => {
                                                        setSavingPhone(true);
                                                        try {
                                                            await updateCardFieldApi(userCard.id, { phoneNumber: phoneValue });
                                                            setUserCard({ ...userCard, phone: phoneValue });
                                                            setEditingPhone(false);
                                                        } catch (err) {
                                                            console.error('Failed to update phone:', err);
                                                        } finally {
                                                            setSavingPhone(false);
                                                        }
                                                    }}
                                                />
                                                <IconButton
                                                    aria-label="Cancel"
                                                    icon={<CloseIcon />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={() => setEditingPhone(false)}
                                                />
                                            </HStack>
                                        ) : (
                                            <Text fontSize="13.5px" fontWeight={600} color={phoneTextColor}>
                                                {userCard.personalTel ? `${userCard.phone} / ${userCard.personalTel}` : userCard.phone}
                                            </Text>
                                        )}
                                    </Box>

                                    <DetailItem label="Department" value={userCard.department} />
                                    <DetailItem label="Official Website" value={userCard.website || 'https://ekopowerlimited.com'} />
                                </Box>

                                {/* Footer Buttons */}
                                <Flex
                                    px={{ base: 6, md: 8 }}
                                    pt={5}
                                    pb={{ base: 6, md: 7 }}
                                    mt={4}
                                    borderTop="1px solid"
                                    borderColor={footerBorder}
                                    gap={3}
                                >
                                    <Button
                                        flex={1}
                                        h="46px"
                                        bg="linear-gradient(135deg, #0B2C6D 0%, #1255C8 100%)"
                                        color="white"
                                        borderRadius="12px"
                                        fontSize="13.5px"
                                        fontWeight={600}
                                        leftIcon={<ViewIcon />}
                                        boxShadow="0 4px 14px rgba(18, 85, 200, 0.25)"
                                        _hover={{
                                            bg: 'linear-gradient(135deg, #0E398C 0%, #1664EC 100%)',
                                            transform: 'translateY(-1px)',
                                        }}
                                        onClick={() => navigate(`/preview/${(userCard as any)._id || userCard.id}`)}
                                    >
                                        Open Digital Card Preview
                                    </Button>

                                    {userCard.status === 'Rejected' && (
                                        <Button
                                            flex={1}
                                            h="46px"
                                            variant="outline"
                                            borderColor="red.400"
                                            color="red.500"
                                            borderRadius="12px"
                                            fontSize="13.5px"
                                            fontWeight={600}
                                            _hover={{ bg: resubmitBtnHoverBg }}
                                            onClick={() => navigate('/create')}
                                        >
                                            Edit &amp; Resubmit
                                        </Button>
                                    )}
                                </Flex>
                            </Box>

                            {/* Approved success banner */}
                            {userCard.status === 'Approved' && (
                                <HStack
                                    spacing={4}
                                    align="flex-start"
                                    bg={approvedBannerBg}
                                    border="1px solid"
                                    borderColor={approvedBannerBorder}
                                    borderRadius="14px"
                                    p={4}
                                >
                                    <Center
                                        w="34px"
                                        h="34px"
                                        borderRadius="10px"
                                        bg={approvedIconBg}
                                        flexShrink={0}
                                    >
                                        <svg width="15" height="15" viewBox="0 0 13 13" fill="none">
                                            <path d="M2 6.5L5.5 10L11 4" stroke="#22C55E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                                        </svg>
                                    </Center>
                                    <Box>
                                        <Text fontSize="13.5px" fontWeight={700} color={approvedTitleColor} mb={0.5}>
                                            Card Approved & Live
                                        </Text>
                                        <Text fontSize="12.5px" color={approvedTextColor} lineHeight={1.5}>
                                            Your card is live across all platforms. Open the card preview to download and share your QR code.
                                        </Text>
                                    </Box>
                                </HStack>
                            )}
                        </VStack>
                    )}

                </VStack>
            </Container>
        </Box>
    );
};

export default UserDashboard;